import { useCallback, useMemo, useState } from 'react';
import { ScannerErrorType, WorkflowState } from '@amzn/awscat-pattern-library-service-model';

import { useQualityCheckService } from '../../contexts/QualityCheckServiceContext';
import { QualityCheckScanResults, TooManyScansError } from '../../services/QualityCheckService';
import { isCommentResolved } from '../v2-common/utils/commentStateUtils';
import { log } from '../../loggers/Logger';
import { ContentValidationError } from '../../services/Errors';
import { ScannerFindingsSummary } from '../../services/ScannerFindings';
import { OpenedEdits } from '../../services/InlineEditsManager';
import { useAuthorPanelSharedState } from './AuthorStatePanelViewModel';
import { ContentValidationIssue, useSaveContent } from '../v2-hooks/useSaveContent';

export interface AuthorQualityCheckStageViewModel {
  readonly stage: 'qualityCheck';
  readonly isBusy: boolean;
  readonly isSaveLoading: boolean;
  readonly isAcceptAllLoading: boolean;
  readonly isScanning: boolean;
  readonly isLoadingFindings: boolean;
  readonly scanResults: QualityCheckScanResults | null;
  readonly openedEdits: OpenedEdits | undefined;
  readonly scannerFindings: readonly ScannerFindingsSummary[];
  /** Outstanding items the author must clear, in display order; empty when nothing remains. */
  readonly requirementsToProceed: readonly string[];
  readonly isAllResolved: boolean;
  readonly isPublisherReviewConfirmationVisible: boolean;
  /** True when the last scan ran past its timeout and may still be running server-side. */
  readonly isScanTimedOut: boolean;
  /** True when the last scan concluded in failure, so the view can surface it. */
  readonly isScanFailed: boolean;
  /** True when the last scan was cancelled, so the view swaps submit for a rescan action. */
  readonly isScanCancelled: boolean;
  /** True when starting another scan is not allowed, so the rescan control must be disabled. */
  readonly isRescanDisabled: boolean;
  readonly scanWarningMessage: string | undefined;
  readonly errorMessage: string | undefined;
  readonly validationIssues: readonly ContentValidationIssue[] | undefined;
  readonly dismissError: () => void;
  readonly dismissPublisherReviewConfirmation: () => void;
  readonly confirmPublisherReview: () => void;
  readonly saveAndClose: () => void;
  readonly acceptAllChanges: () => void;
  readonly startQualityCheck: () => void;
  readonly startPublisherReview: () => void;
  readonly scanFeedbackComposeEvent:
    | ((value: string, feedback?: string) => Record<string, unknown>)
    | undefined;
}

export function useAuthorQualityCheckStageViewModel(): AuthorQualityCheckStageViewModel {
  const sharedState = useAuthorPanelSharedState(WorkflowState.QUALITY_CHECK);
  const { setBusyAction, setErrorMessage, navigateToViewer, contentId, workflow } = sharedState;
  const qualityCheckService = useQualityCheckService();
  const { save } = useSaveContent();
  const [isPublisherReviewConfirmationVisible, setPublisherReviewConfirmationVisible] =
    useState(false);

  // Errors from start quality check operations, merged with the save-flow error below.
  const [startQualityCheckError, setStartQualityCheckError] = useState<
    { readonly message: string; readonly issues?: readonly ContentValidationIssue[] } | undefined
  >(undefined);

  // Depend on the stable function, not the whole sharedState object, which is a
  // fresh literal each render and would defeat this and every dependent callback's memoization.
  const { dismissError: sharedDismissError } = sharedState;

  // Dismissing clears both error sources so neither can linger behind the other.
  const dismissError = useCallback(() => {
    sharedDismissError();
    setStartQualityCheckError(undefined);
  }, [sharedDismissError]);

  const acceptAllChanges = useCallback(async () => {
    if (!workflow) return;
    setBusyAction('acceptAll');
    dismissError();
    try {
      await qualityCheckService.acceptAllChanges();
    } catch (err) {
      log.error({ err, contentId }, 'Failed to accept all changes');
      setErrorMessage('Failed to accept all changes. Please try again.');
    } finally {
      setBusyAction(null);
    }
  }, [setBusyAction, setErrorMessage, dismissError, contentId, workflow, qualityCheckService]);

  const startQualityCheck = useCallback(async () => {
    if (!workflow || !contentId) {
      log.warn({ contentId }, 'Cannot start quality check: content not loaded');
      return;
    }
    setBusyAction('qualityCheck');
    dismissError();
    try {
      await qualityCheckService.start(contentId, workflow.draftWorkflowId);
    } catch (err) {
      // A validation failure lists exactly what the author must fix before the
      // scan can run, so surface the issues instead of a generic retry message.
      if (err instanceof ContentValidationError) {
        setStartQualityCheckError({
          message: `Failed to start quality check: ${err.message}`,
          issues: err.issues,
        });
      } else if (err instanceof TooManyScansError) {
        // Throttled by the backend; the error already carries the wait-and-retry copy.
        setStartQualityCheckError({ message: err.message });
      } else {
        log.error({ err, contentId }, 'Failed to start quality check');
        setStartQualityCheckError({ message: 'Failed to start quality check. Please try again.' });
      }
    } finally {
      setBusyAction(null);
    }
  }, [setBusyAction, dismissError, contentId, workflow, qualityCheckService]);

  const submitForPublisherReview = useCallback(async () => {
    if (!workflow || !contentId) {
      log.warn({ contentId }, 'Cannot submit for publisher review: content not loaded');
      return;
    }
    setBusyAction('publisherReview');
    dismissError();

    // Persist any unsaved edits so the publisher reviews the latest content.
    const saveResult = await save();
    if (!saveResult.ok) {
      setStartQualityCheckError({
        message: saveResult.errorMessage,
        issues: saveResult.validationIssues,
      });
      setBusyAction(null);
      return;
    }

    try {
      await qualityCheckService.submitForPublisherReview(contentId, workflow.draftWorkflowId);
      navigateToViewer();
    } catch (err) {
      log.error({ err, contentId }, 'Failed to submit for publisher review');
      setErrorMessage('Failed to submit for publisher review. Please try again.');
    } finally {
      setBusyAction(null);
    }
  }, [
    setBusyAction,
    setErrorMessage,
    dismissError,
    navigateToViewer,
    contentId,
    workflow,
    qualityCheckService,
    save,
  ]);

  // Any outcome other than a clean completion (failed, cancelled, timed out, or a failed scanner)
  // means the checks didn't pass, so the author should confirm before submitting.
  const hasUnpassedQualityChecks =
    (sharedState.scanResults != null && sharedState.scanResults.status !== 'completed') ||
    sharedState.scannerFindings.some((finding) => finding.status === 'failed');

  // A timed-out scan may still be running server-side, so block submitting until the
  // author reloads the page to pick up its real outcome.
  const isScanTimedOut = sharedState.scanResults?.status === 'timed_out';

  // A failed scan produces no findings, so the findings table alone reads as "no
  // issues"; flag it so the view can surface the failure explicitly.
  const isScanFailed = sharedState.scanResults?.status === 'failed';

  // A cancelled scan produced no verdict, so the author must rescan before submitting.
  const isScanCancelled = sharedState.scanResults?.status === 'cancelled';

  // When the author submits with unpassed checks, then ask for confirmation first; otherwise submit.
  const startPublisherReview = useCallback(() => {
    if (hasUnpassedQualityChecks) {
      setPublisherReviewConfirmationVisible(true);
      return;
    }
    void submitForPublisherReview();
  }, [hasUnpassedQualityChecks, submitForPublisherReview]);

  // When the author dismisses the confirmation, then close it without submitting.
  const dismissPublisherReviewConfirmation = useCallback(
    () => setPublisherReviewConfirmationVisible(false),
    [],
  );

  // When the author confirms, then close the confirmation and submit for review.
  const confirmPublisherReview = useCallback(() => {
    setPublisherReviewConfirmationVisible(false);
    void submitForPublisherReview();
  }, [submitForPublisherReview]);

  // When there are both errors from quality check and shared state (which unlikely to occur),
  // Show the shared state errors first.
  const activeError =
    sharedState.errorMessage !== undefined
      ? { message: sharedState.errorMessage, issues: sharedState.validationIssues }
      : startQualityCheckError;
  const errorMessage = activeError?.message;
  const validationIssues = activeError?.issues;

  const unresolvedCommentCount = useMemo(
    () =>
      sharedState.scanResults?.scanComments?.filter((c) => !isCommentResolved(c.state)).length ?? 0,
    [sharedState.scanResults],
  );
  const unresolvedEditCount = sharedState.openedEdits?.unresolvedCount ?? 0;

  // Only the requirements that still apply, in the order they should be shown.
  const requirementsToProceed: readonly string[] = [
    ...(isScanTimedOut ? ['Wait for the quality check scan to finish'] : []),
    ...(unresolvedEditCount > 0 ? ['Resolve all text changes'] : []),
    ...(unresolvedCommentCount > 0 ? ['Resolve all comments'] : []),
  ];

  // Rescanning while the service is not idle silently no-ops: QualityCheckService.start
  // early-returns whenever _state is 'scan_in_progress' or 'loading_results', and throws
  // for a timed-out scan. Gate the control on all three so the click cannot land during
  // the window where the panel has rendered the completed findings table but the service
  // has not yet transitioned back to idle from handleScanComplete.
  const isRescanDisabled =
    isScanTimedOut || sharedState.isBusy || sharedState.isScanning || sharedState.isLoadingFindings;

  const isAllResolved =
    sharedState.scanResults !== null && unresolvedEditCount === 0 && unresolvedCommentCount === 0;

  const scanWarningMessage = sharedState.openedEdits?.errors.some(
    (error) => error.errorType === ScannerErrorType.GUARDRAIL_INTERVENED,
  )
    ? 'The quality check was stopped because some content triggered safety controls. Review and remove any potentially harmful or inappropriate material, then rescan.'
    : undefined;

  const traceId =
    sharedState.scanResults?.status !== 'failed' ? sharedState.scanResults?.scanId : undefined;
  const scanFeedbackComposeEvent = useMemo(
    () =>
      traceId
        ? (value: string, feedback?: string) => ({
            subType: 'QualityCheckScanFeedback',
            contentId: sharedState.contentId ?? '',
            workflowId: sharedState.workflow?.draftWorkflowId ?? 0,
            traceId,
            rating: value,
            ...(feedback != null && { feedback }),
          })
        : undefined,
    [traceId, sharedState.contentId, sharedState.workflow?.draftWorkflowId],
  );

  return {
    stage: 'qualityCheck',
    isBusy: sharedState.isBusy,
    isSaveLoading: sharedState.isSaveLoading,
    isAcceptAllLoading: sharedState.busyAction === 'acceptAll',
    isScanning: sharedState.isScanning,
    isLoadingFindings: sharedState.isLoadingFindings,
    scanResults: sharedState.scanResults,
    openedEdits: sharedState.openedEdits,
    scannerFindings: sharedState.scannerFindings,
    requirementsToProceed,
    isAllResolved,
    isPublisherReviewConfirmationVisible,
    isScanTimedOut,
    isScanFailed,
    isScanCancelled,
    isRescanDisabled,
    scanWarningMessage,
    errorMessage,
    validationIssues,
    dismissError,
    dismissPublisherReviewConfirmation,
    confirmPublisherReview,
    saveAndClose: sharedState.saveAndClose,
    acceptAllChanges,
    startQualityCheck,
    startPublisherReview,
    scanFeedbackComposeEvent,
  };
}
